import { createFileRoute } from '@tanstack/react-router';
import { Alert, Stack } from '@mantine/core';
import { AlertTriangle, BookOpen } from 'lucide-react';
import { useState } from 'react';
import { PageContainer, PageHeader, Skeleton } from '../components/ui';
import { LessonView } from '../components/lessons/LessonView';
import { useLesson, useMarkLessonComplete } from '../hooks/useLessons';

/**
 * `/courses/{slug}/{module}` — LessonView page (PLAN.md §Phase 4.5).
 *
 * Resolves the lesson via `useLesson` (course-detail-backed; see hook docs),
 * delegates rendering + interaction to `LessonView`, and wires the
 * "Mark complete" mutation so trainees can advance through a module.
 */

export const Route = createFileRoute('/courses/$slug/$module')({
  component: LessonPage,
});

function LessonPage() {
  const { slug, module: moduleId } = Route.useParams();
  const { data, isPending, isError, error } = useLesson(slug, moduleId);
  const markComplete = useMarkLessonComplete();
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  if (isPending) {
    return (
      <PageContainer size="default">
        <Stack gap="lg">
          <Skeleton height={36} width="40%" />
          <Skeleton height={20} width="20%" />
          <Skeleton height={320} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="default">
        <PageHeader
          icon={BookOpen}
          title="Module"
          description="Lesson content and exercises."
          breadcrumbs={[
            { label: 'Forge', to: '/' },
            { label: 'Courses', to: '/courses' },
            { label: 'Module' },
          ]}
        />
        <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Could not load lesson">
          {(error as Error)?.message ?? 'Lesson not found'}
        </Alert>
      </PageContainer>
    );
  }

  const handleMarkComplete = () => {
    setFeedbackError(null);
    markComplete.mutate(
      { courseSlug: slug, moduleId },
      {
        onError: (err) => setFeedbackError(err.message),
      },
    );
  };

  return (
    <PageContainer size="default">
      <PageHeader
        icon={BookOpen}
        title={data.module.title}
        description="Lesson content and exercises."
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Courses', to: '/courses' },
          { label: 'Module' },
        ]}
      />
      <LessonView
        data={data}
        onMarkComplete={handleMarkComplete}
        isCompleting={markComplete.isPending}
        completeError={feedbackError}
      />
    </PageContainer>
  );
}
